import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Badge } from '@frontend/shadcn/components/ui/badge';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { DataTable, TablePagination } from '@frontend/app/refresh-components/DataTableV2';
import { ArrowDownToLineIcon, TableColumnsIcon } from '@revfluence/fresh-icons/regular/esm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faUnlock,
  faFileContract,
  faGift,
  faHandshake,
  faUserCheck,
  faUserXmark,
  faReply,
  faImage,
  faShoppingCart,
} from '@fortawesome/free-solid-svg-icons';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@frontend/shadcn/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@frontend/shadcn/components/ui/popover';
import { Checkbox } from '@frontend/shadcn/components/ui/checkbox';
import { isEmpty } from 'lodash';
import { format } from 'date-fns';

import {
  useListOfferEmailTemplatesQuery,
  useListOfferEmailAutomationsQuery,
} from '@affiliates/hooks';
import { AutomationBlueprintName } from '@frontend/app/containers/Projects/AutomationConfig/types';
import { useListExecutions } from '@frontend/app/hooks/automations';
import { AutomationExecutionStatus } from '@frontend/app/types/globalTypes';
import {
  ListTemplates_listTemplates_results,
} from '@frontend/app/queries/types/ListTemplates';
import {
  ListAutomations_listAutomations_results,
} from '@frontend/app/queries/types/ListAutomations';
import { OrderingDirection } from '@frontend/app/types/globalTypes';
import { AutomationProvidersEnum, AutomationStatus, AutomationTemplateTypesEnum } from '@services/automations/types';
import { logger } from '@common';

// Props for the component
interface EmailAutomationsProps {
  offerId: string;
  baseUri: string;
  isProjectFlow?: boolean;
  isTitleRequired?: boolean;
  isProductCatalogActionDisabled?: boolean;
}

// Table data structure (simple local interface)
interface EmailAutomationTableData {
  id: string;
  templateId: string;
  name: string;
  icon: React.ReactNode;
  totalRuns: number;
  successRate: number | string;
  status: 'Active' | 'Inactive' | 'Paused' | 'Draft';
  lastUpdated: Date | string ;
  isDisabled?: boolean;
}

// Helper functions
const getAutomationIcon = (metadata: any): React.ReactNode => {
  const icon = metadata?.icon;

  switch (icon) {
    case 'tier_unlock':
      return (
        <div className="w-8 h-8 rounded-full bg-purple-5 flex items-center justify-center">
          <FontAwesomeIcon icon={faUnlock} className="h-4 w-4 text-grey-0" />
        </div>
      );
    case 'contract':
      return (
        <div className="w-8 h-8 rounded-full bg-blue-5 flex items-center justify-center">
          <FontAwesomeIcon icon={faFileContract} className="h-4 w-4 text-grey-0" />
        </div>
      );
    case 'reward_unlock':
      return (
        <div className="w-8 h-8 rounded-full bg-green-5 flex items-center justify-center">
          <FontAwesomeIcon icon={faGift} className="h-4 w-4 text-grey-0" />
        </div>
      );
    case 'offer_welcome_email':
      return (
        <div className="w-8 h-8 rounded-full bg-red-5 flex items-center justify-center">
          <FontAwesomeIcon icon={faHandshake} className="h-4 w-4 text-grey-0" />
        </div>
      );
    case 'user_rejected':
      return (
        <div className="w-8 h-8 rounded-full bg-red-5 flex items-center justify-center">
          <FontAwesomeIcon icon={faUserXmark} className="h-4 w-4 text-grey-0" />
        </div>
      );
    case 'envelope':
      return (
        <div className="w-8 h-8 rounded-full bg-blue-5 flex items-center justify-center">
          <FontAwesomeIcon icon={faEnvelope} className="h-4 w-4 text-grey-0" />
        </div>
      );
    case 'follow_up':
      return (
        <div className="w-8 h-8 rounded-full bg-teal-5 flex items-center justify-center">
          <FontAwesomeIcon icon={faReply} className="h-4 w-4 text-grey-0" />
        </div>
      );
    case 'image':
      return (
        <div className="w-8 h-8 rounded-full bg-pink-5 flex items-center justify-center">
          <FontAwesomeIcon icon={faImage} className="h-4 w-4 text-grey-0" />
        </div>
      );
    case 'shopping':
      return (
        <div className="w-8 h-8 rounded-full bg-lime-5 flex items-center justify-center">
          <FontAwesomeIcon icon={faShoppingCart} className="h-4 w-4 text-grey-0" />
        </div>
      );
    case 'user_accepted':
      return (
        <div className="w-8 h-8 rounded-full bg-green-5 flex items-center justify-center">
          <FontAwesomeIcon icon={faUserCheck} className="h-4 w-4 text-grey-0" />
        </div>
      );
    default:
      return (
        <div className="w-8 h-8 rounded-full bg-magenta-5 flex items-center justify-center">
          <FontAwesomeIcon icon={faEnvelope} className="h-4 w-4 text-grey-0" />
        </div>
      );
  }
};

export const determineAutomationStatus = (
  automation?: ListAutomations_listAutomations_results
): 'Active' | 'Inactive' | 'Paused' | 'Draft' => {
  if (!automation) {
    return 'Inactive';
  }

  switch (automation.status) {
    case AutomationStatus.ACTIVE:
      return 'Active';
    case AutomationStatus.DRAFT:
      return 'Draft';
    case AutomationStatus.DISABLED:
      return 'Paused';
    default:
      return 'Inactive';
  }
};

const StatusBadge: React.FC<{ status: EmailAutomationTableData['status'] }> = ({ status }) => {
  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800 hover:bg-green-100';
      case 'Paused':
        return 'bg-yellow-100 text-orange-600 hover:bg-yellow-100';
      case 'Draft':
        return 'bg-blue-100 text-blue-800 hover:bg-blue-100';
      case 'Inactive':
      default:
        return 'bg-gray-100 text-gray-600 hover:bg-gray-100';
    }
  };

  return (
    <Badge variant="secondary" className={getStatusVariant(status)}>
      {status}
    </Badge>
  );
};

const SortButton = ({ column, children }: { column: any; children: React.ReactNode }) => {
  return (
    <Button
      variant="ghost"
      className="px-2 hover:bg-transparent w-full justify-start"
      onClick={() => column.toggleSorting()}
    >
      {children}
    </Button>
  );
};

export const EmailAutomations: React.FC<EmailAutomationsProps> = ({
  offerId,
  baseUri,
  isProjectFlow,
  isTitleRequired = true,
  isProductCatalogActionDisabled = false
}) => {
  const history = useHistory();
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // Fetch templates data
  const {
    data: templatesResponse,
    loading: templatesLoading,
    error: templatesError
  } = useListOfferEmailTemplatesQuery({
    variables: {
      paging: { offset: (page - 1) * pageSize, limit: pageSize },
      filters: { type: isProjectFlow ? null : AutomationTemplateTypesEnum.OFFER_EMAIL_TEMPLATES },
    }
  });

  // Fetch automations data
  const {
    data: automationsResponse,
    loading: automationsLoading,
    error: automationsError
  } = useListOfferEmailAutomationsQuery({
    variables: {
      paging: { offset: (page - 1) * pageSize, limit: pageSize },
      filters: {
        providerId: isProjectFlow ? null : Number(offerId),
        providerName: isProjectFlow ? null : AutomationProvidersEnum.STA_OFFER_EMAILS,
        program: {
          id: isProjectFlow ? Number(offerId) : undefined,
        },
      },
      ordering: { name: OrderingDirection.ASC }
    }
  });

  // Fetch executions data for all automations to calculate stats
  const automationIds = automationsResponse?.listAutomations.results.map((auto: any) => auto.id) || [];
  const {
    data: executionsResponse,
    loading: executionsLoading,
    error: executionsError,
  } = useListExecutions({
    variables: {
      paging: { offset: (page - 1) * pageSize, limit: pageSize },
      filters: {
        automation: {
          providerId: isProjectFlow ? null : Number(offerId),
          providerName: isProjectFlow ? null : AutomationProvidersEnum.STA_OFFER_EMAILS,
          program: {
            id: isProjectFlow ? Number(offerId) : undefined,
          },
        },
      },
    },
    skip: automationIds.length === 0, // Skip if no automations
  });
  // Transform and combine data
  const data = useMemo(() => {
    if (!templatesResponse?.listTemplates.results) return [];

    const templates = templatesResponse.listTemplates.results;
    const automations = automationsResponse?.listAutomations.results || [];
    const executions = executionsResponse?.listExecutions.results || [];

    return templates.map((template: ListTemplates_listTemplates_results): EmailAutomationTableData => {
      // Check if this template should be disabled
      const isDisabled = isProjectFlow &&
        template.blueprint.name === AutomationBlueprintName.SendProductOrderRequestAfterBriefIsAccepted &&
        isProductCatalogActionDisabled;
      // Find corresponding automation
      const automation = automations.find((auto: ListAutomations_listAutomations_results) => auto.templateId === template.id);

      // Calculate execution stats if automation exists
      let totalRuns = 0;
      let successRate = 0;

      if (automation?.id) {
        // Filter executions for this specific automation
        const automationExecutions = executions.filter(
          (exec: any) => exec.automationId === automation.id
        );

        totalRuns = automationExecutions.length;

        if (totalRuns > 0) {
          // Count successful executions (COMPLETED status)
          const successfulRuns = automationExecutions.filter(
            (exec: any) => exec.status === AutomationExecutionStatus.COMPLETED
          ).length;

          // Calculate success rate as percentage
          successRate = Math.round((successfulRuns / totalRuns) * 100);
        }
      }

      // Safe date parsing with logging
      const parseDate = (dateString?: string): Date | string => {
        if (!dateString) {
          return '-';
        }

        const parsedDate = new Date(dateString);
        if (isNaN(parsedDate.getTime())) {
          logger.error('Invalid date string:', dateString);
          return '-';
        }

        return parsedDate;
      };

      try {
        return {
          id: template.id,
          templateId: template.id,
          name: template.blueprint.name || 'Unknown Template',
          icon: getAutomationIcon(template.blueprint.metadata),
          totalRuns,
          successRate: totalRuns > 0 ? successRate : '-',
          status: determineAutomationStatus(automation),
          lastUpdated: parseDate(automation?.updatedDate),
          isDisabled,
        };
      } catch (error) {
        console.error('Error transforming template:', template, error);
        // Return a fallback object
        return {
          id: template.id || 'unknown',
          templateId: template.id || 'unknown',
          name: template.blueprint.name || 'Unknown Template',
          icon: '📧',
          totalRuns: 0,
          successRate: 0,
          status: 'Inactive' as const,
          lastUpdated: '-',
          isDisabled: false,
        };
      }
    });
  }, [templatesResponse, automationsResponse, executionsResponse, isProjectFlow, isProductCatalogActionDisabled]);

  const isLoading = templatesLoading || automationsLoading || executionsLoading;
  const hasError = templatesError || automationsError || executionsError;

  const columns = useMemo(() => [
    {
      id: 'automation',
      headerName: 'Automation',
      accessorKey: 'name',
      cell: ({ row }) => (
        <div className={`flex items-center gap-5 ${row.original.isDisabled ? 'opacity-50' : 'cursor-pointer hover:underline'}`}>
          <div className="w-5 flex-shrink-0">
            {row.original.icon}
          </div>
          <span className={`font-medium ${row.original.isDisabled ? 'text-grey-5' : 'text-grey-6'}`}>
            {row.original.name}
          </span>
          {row.original.name === 'X sales amount away from next tier' && (
            <div className="w-4 h-4 rounded-full bg-red-500 flex items-center justify-center">
              <span className="text-white text-xs font-bold">!</span>
            </div>
          )}
        </div>
      ),
      header: ({ column }) => (
        <SortButton column={column}>Automation</SortButton>
      ),
      size: 300,
      minSize: 300,
    },
    {
      id: 'totalRuns',
      headerName: 'Total Runs',
      accessorKey: 'totalRuns',
      cell: ({ row }) => (
        <div className={`text-right ${row.original.isDisabled ? 'opacity-50' : ''}`}>
          {row.original.totalRuns > 0 ? row.original.totalRuns.toLocaleString() : '-'}
        </div>
      ),
      header: ({ column }) => (
        <SortButton column={column}>Total Runs</SortButton>
      ),
      size: 150,
      minSize: 150,
    },
    {
      id: 'successRate',
      headerName: 'Success Rate',
      accessorKey: 'successRate',
      cell: ({ row }) => (
        <div className={`text-right flex items-center justify-end gap-2 ${row.original.isDisabled ? 'opacity-50' : ''}`}>
          {row.original.successRate !== '-' ? (
            <>
              <span>{row.original.successRate}%</span>
              <div className="w-1 h-1 rounded-full bg-purple-500" />
            </>
          ) : (
            <span>-</span>
          )}
        </div>
      ),
      header: ({ column }) => (
        <SortButton column={column}>Success Rate</SortButton>
      ),
      size: 150,
      minSize: 150,
    },
    {
      id: 'status',
      headerName: 'Status',
      accessorKey: 'status',
      cell: ({ row }) => (
        <div className={row.original.isDisabled ? 'opacity-50' : ''}>
          <StatusBadge status={row.original.isDisabled ? 'Inactive' : row.original.status} />
        </div>
      ),
      header: ({ column }) => (
        <SortButton column={column}>Status</SortButton>
      ),
      size: 120,
      minSize: 120,
    },
    {
      id: 'lastUpdated',
      headerName: 'Last Updated',
      accessorKey: 'lastUpdated',
      cell: ({ row }) => {
        const automation = row.original;
        const { lastUpdated } = automation;

        // Handle both string '-' and Date objects
        const displayDate = () => {
          if (!lastUpdated || lastUpdated === '-') return '-';
          if (lastUpdated instanceof Date && !isNaN(lastUpdated.getTime())) {
            return format(lastUpdated, 'MMM d, yyyy');
          }
          return '-';
        };

        return (
          <div className={`flex items-center gap-2 ${row.original.isDisabled ? 'opacity-50' : ''}`}>
            <span>{displayDate()}</span>
          </div>
        );
      },
      header: ({ column }) => (
        <SortButton column={column}>Last Updated</SortButton>
      ),
      size: 180,
      minSize: 180,
    },
  ], []);

  const totalCount = templatesResponse?.listTemplates.paging.count || 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  // Data is already paginated from API, no need to slice
  const paginatedData = useMemo(() => data, [data]);

  const visibleColumns = useMemo(
    () => columns.filter((col) => columnVisibility[col.id] !== false),
    [columns, columnVisibility]
  );

  const exportToCSV = () => {
    const headers = columns.map((col) => col.headerName).join(',');
    const rows = data.map((row) => {
      // Format the date and wrap in quotes to handle comma in date
      const formattedDate = !row.lastUpdated || row.lastUpdated === '-' ? '"-"' : `"${format(new Date(row.lastUpdated), 'MMM d, yyyy')}"`;

      return [
        row.name,
        row.totalRuns,
        row.successRate !== '-' ? `${row.successRate}%` : '-',
        row.status,
        formattedDate
      ].join(',');
    });

    const csvContent = [headers, ...rows].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'email-automations.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle row click
  const handleRowClick = (row: EmailAutomationTableData) => {
    // Don't navigate if row is disabled
    if (row.isDisabled) {
      return;
    }

    const templateId = row.templateId;
    const automationId = automationsResponse?.listAutomations.results.find((auto: ListAutomations_listAutomations_results) => auto.templateId === templateId)?.id;
    if (isProjectFlow) {
      history.push(`/projects/${offerId}/automation_v2/${automationId ? `automation/${automationId}` : `template/${templateId}`}`);
      return;
    }
    // Navigate to EmailAutomationBuilder with templateId and offerId
    // Use baseUri to construct the full path
    history.push(`${baseUri}/offers/${offerId}/offer_automation/${templateId}${automationId ? `?automationId=${automationId}` : ''}`);
  };

  // Show loading state
  if (isLoading) {
    return (
      <div>
        <div className="mb-6">
          {isTitleRequired && (
            <>
              <h1 className="text-base font-medium text-grey-6 mb-2">Automation</h1>
              <p className="text-grey-5 text-sm">Automate email tasks to easily scale your offers.</p>
            </>
          )}
        </div>
        <div className="w-full border-grey-2 rounded-2xl overflow-hidden border-solid border-[1px] flex flex-col">
          <div className="w-full bg-white flex justify-between items-center px-4 py-2 border-b border-grey-2">
            <Skeleton className="h-4 w-32" />
            <div className="flex items-center gap-2">
              <Skeleton className="h-8 w-8" />
              <Skeleton className="h-8 w-8" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-8 w-20" />
            </div>
          </div>
          <div className="flex-1 p-6">
            {/* Table skeleton */}
            <div className="space-y-3">
              {/* Table header */}
              <div className="grid grid-cols-5 gap-4 py-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-24" />
              </div>

              {/* Table rows */}
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="grid grid-cols-5 gap-4 py-3 border-t">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-8 w-8 rounded-full" />
                    <Skeleton className="h-4 w-32" />
                  </div>
                  <Skeleton className="h-4 w-12" />
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-6 w-20 rounded-full" />
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-4 w-20" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (hasError) {
    return (
      <div>
        <div className="mb-6">
          {isTitleRequired && (
            <>
              <h1 className="text-base font-medium text-grey-6 mb-2">Automation</h1>
              <p className="text-grey-5 text-sm">Automate email tasks to easily scale your offers.</p>
            </>
          )}
        </div>
        <div className="w-full border-grey-2 rounded-2xl overflow-hidden border-solid border-[1px] flex flex-col">
          <div className="p-8 text-center">
            <div className="text-sm text-red-600">
              Error loading email automations. Please try again.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        {isTitleRequired && (
          <>
            <h1 className="text-base font-medium text-grey-6 mb-2">Automation</h1>
            <p className="text-grey-5 text-sm">Automate email tasks to easily scale your offers.</p>
          </>
        )}
      </div>

      <div className="w-full border-grey-2 rounded-2xl overflow-hidden border-solid border-[1px] flex flex-col">
        <div className="w-full bg-white flex justify-between items-center px-4 py-2 border-b border-grey-2">
          <div className="text-sm text-muted-foreground">
            {totalCount} Automation
          </div>
          <div className="flex items-center gap-2">
            {/* Export Button */}
            <Tooltip>
              <TooltipTrigger>
                <Button
                  onClick={exportToCSV}
                  variant="ghost"
                  size="icon"
                  disabled={isEmpty(data)}
                  aria-label="Export automations"
                >
                  <ArrowDownToLineIcon className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium">Export automations</TooltipContent>
            </Tooltip>

            {/* Column Visibility */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={isEmpty(data)}
                  aria-label="Toggle column visibility"
                >
                  <TableColumnsIcon className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-56 p-4">
                <div className="flex flex-col gap-2">
                  <h4 className="font-medium mb-2">Visible Columns</h4>
                  {columns.map((column) => (
                    <label key={column.id} className="flex items-center gap-2">
                      <Checkbox
                        checked={columnVisibility[column.id] !== false}
                        onCheckedChange={(checked) => {
                            setColumnVisibility((prev) => ({
                              ...prev,
                              [column.id]: checked === true,
                            }));
                          }}
                      />
                      <span>{column.headerName}</span>
                    </label>
                    ))}
                </div>
              </PopoverContent>
            </Popover>

            {/* Pagination Controls */}
            <TablePagination
              pageIndex={page - 1}
              pageSize={pageSize}
              totalCount={totalCount}
              canPreviousPage={page > 1}
              canNextPage={page < totalPages}
              onPaginationChange={(newPage) => setPage(newPage + 1)}
            />
          </div>
        </div>
        <div className="flex-1 overflow-hidden">
          <DataTable
            columns={visibleColumns}
            data={paginatedData}
            enableColumnResizing
            getRowId={(row) => row.id}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            onRowClick={handleRowClick}
            className="h-full"
            removeBorderBottom
          />
        </div>
      </div>
    </div>
  );
};
